import React, { useMemo } from "react";
import { Image, Stack } from "react-bootstrap";
import SolidPlusIcon from "../../icons/SolidPlusIcon";
import SolidMinusIcon from "../../icons/SolidMinusIcon";
import ImageCheckmarkCircle from "../../../images/checkmark-circle-black.svg";
import ImagePendingIcon from "../../../images/pending-icon.svg";
import ImageFailedIcon from "../../../images/x-circle-white.svg";
import { ROUTES } from "../../../utils/constants";
import {
  TransactionHistory,
  TransactionStatus,
  TransactionType,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { TransactionCategory } from "../types";
import { formatTransactionDate } from "../../../utils/helpers";
import "./TransactionItem.scss";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

export default function TransactionItem(props: TransactionHistory) {
  const { id, type, currency, amount, status, createdAt } = props;
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const statusIcon = useMemo(() => {
    switch (status) {
      case TransactionStatus.SUCCESS:
        return ImageCheckmarkCircle;
      case TransactionStatus.PENDING:
      case TransactionStatus.IN_PROGRESS:
        return ImagePendingIcon;
      default:
        return ImageFailedIcon;
    }
  }, [status]);

  const transactionCategory = useMemo(() => {
    switch (type) {
      case TransactionType.TOPUP:
      case TransactionType.REWARD:
      case TransactionType.CRYPTO_DEPOSIT:
        return TransactionCategory.CREDIT;
      default:
        return TransactionCategory.DEBIT;
    }
  }, [type]);

  const transactionTitle = useMemo(() => {
    switch (type) {
      case TransactionType.CRYPTO_DEPOSIT:
        return "Deposit";
      case TransactionType.CRYPTO_WITHDRAWAL:
        return "Withdraw";
      case TransactionType.TOPUP:
        return "Top Up";
      case TransactionType.CASHOUT:
        return "Cash Out";
      case TransactionType.REVERSAL:
        return "Reversal";
      case TransactionType.REWARD:
        return "Reward";
      case TransactionType.INTERNAL_DEBIT:
        return "Account Debited";
      case TransactionType.TRANSFER:
        return "Transfer";
      case TransactionType.STAKE:
      default:
        return "Enter Battle";
    }
  }, [type]);

  const displayAmount = useMemo(() => {
    const sign = transactionCategory === TransactionCategory.CREDIT ? "+" : "-";
    if (isFiatDisplay) {
      return `${sign}${formatHprToFiatValue(amount)}`;
    }
    return `${sign}${amount} ${currency}`;
  }, [
    isFiatDisplay,
    transactionCategory,
    amount,
    currency,
    formatHprToFiatValue,
  ]);
  return (
    <a
      href={`${ROUTES.WALLET.TRANSACTION_HISTORY}/${id}`}
      className="d-flex justify-content-between TransactionItem"
    >
      <Stack direction="horizontal" gap={2}>
        <div className="rounded-circle TransactionIcon">
          {transactionCategory === TransactionCategory.CREDIT ? (
            <SolidPlusIcon />
          ) : (
            <SolidMinusIcon />
          )}
          <Image
            src={statusIcon}
            width={12}
            height={12}
            className="StatusIcon"
          />
        </div>
        <Stack className="align-items-start TransactionTitleDate">
          <p className="text-capitalize Grotesk SpecialFontSettings TransactionTitle">
            {transactionTitle}
          </p>
          <p className="Grotesk TransactionDate">
            {formatTransactionDate(createdAt)}
          </p>
        </Stack>
      </Stack>
      <p
        className={`Grotesk TransactionAmount ${
          transactionCategory === TransactionCategory.CREDIT
            ? "Credit"
            : "Debit"
        }`}
      >
        {displayAmount}
      </p>
    </a>
  );
}
